"use client";

import { type VariantProps, cva } from "class-variance-authority";
import { Gamepad2, Heart } from "lucide-react";
import Image from "next/image";
import * as React from "react";

import type { CampaignQuestState } from "@/components/QuestsV2/types";
import { useQuestCompletions } from "@/components/QuestsV2/useQuestCompletions";
import {
  METATOY_GAME_UID,
  METATOY_TWITCH_DROPS_DATE,
  METATOY_TWITCH_DROPS_END_DATE,
} from "@/components/layout/GameDropsLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Action } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { getUnlockedBadge } from "./badge-registry";
import { DisplayBadge } from "./display-badge";
import {
  type GetGameDetailsAPIResponse,
  type SocialStatAPIResponse,
  formatQuestNumber,
} from "./types";
import useGameDetailsAnalytics from "./useGameDetailsAnalytics";

// Types
interface SocialStatProps {
  icon: React.ReactNode;
  value: string;
  label: string;
}

const mapSocialStat = (stat: SocialStatAPIResponse): SocialStatProps => {
  const value = formatQuestNumber(stat.count);
  let label = "";
  let icon: { path: string; alt: string } | undefined;
  switch (stat.type) {
    case "x":
      label = "Followers";
      icon = { path: "/images/quests/x.svg", alt: "X" };
      break;
    case "discord":
      label = "Members";
      icon = { path: "/images/quests/discord.svg", alt: "Discord" };
      break;
    case "telegram":
      label = "Members";
      icon = { path: "/images/quests/telegram-logo.svg", alt: "Telegram" };
      break;
  }
  return {
    icon: icon ? (
      <Image src={icon.path} alt={icon.alt} width={8} height={8} />
    ) : undefined,
    label: label,
    value: value,
  };
};

// Reusable ActionButtons component - used in both mobile (vertical) and desktop (horizontal) layouts
const ActionButtons = React.forwardRef<
  HTMLDivElement,
  {
    className?: string;
    orientation?: "horizontal" | "vertical";
    onPlayClick: () => void;
    playUrl?: string;
    isWishlisted?: boolean;
    toggleWishlist?: () => void;
    handleTwitchDropsClick?: () => void;
    twitchDropsLive?: boolean;
  }
>(
  (
    {
      className,
      orientation = "horizontal",
      onPlayClick,
      playUrl,
      isWishlisted,
      toggleWishlist,
      handleTwitchDropsClick,
      twitchDropsLive,
    },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        "flex gap-2.5 items-center",
        orientation === "vertical" ? "flex-col" : "flex-row",
        className,
      )}
    >
      {playUrl && (
        <Link href={playUrl} target="_blank" onClick={onPlayClick}>
          <Button variant="outline" size="icon" aria-label="Play game">
            <Gamepad2 className="h-4 w-4" />
          </Button>
        </Link>
      )}
      <Button
        variant={isWishlisted ? "default" : "outline"}
        size="icon"
        onClick={toggleWishlist}
        aria-label="Follow"
      >
        <Heart className="h-4 w-4" />
      </Button>
      {twitchDropsLive && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleTwitchDropsClick}
          aria-label="Twitch"
          className="animate-golden-glow"
          style={{ animationDuration: "1s" }}
        >
          <Image
            src="/images/quests/twitch-logo.svg"
            alt="Twitch logo"
            width={15}
            height={15}
          />
        </Button>
      )}
    </div>
  ),
);

// Reusable CategoryBadges component - used in both desktop (beside logo) and mobile (centered) layouts
const CategoryBadges = React.forwardRef<
  HTMLDivElement,
  { categories: string[]; className?: string }
>(({ categories, className }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-wrap gap-2.5 items-center", className)}
  >
    {categories.map((category) => (
      <Badge key={category} variant="outline">
        {category.charAt(0).toUpperCase() + category.slice(1)}
      </Badge>
    ))}
  </div>
));

// CVA variants for PageTitle
const pageTitleVariants = cva("@container border-b border-border", {
  variants: {
    variant: {
      default: "",
      compact: "",
      expanded: "",
    },
    size: {
      default: "",
      sm: "",
      lg: "",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

const contentVariants = cva("text-white", {
  variants: {
    size: {
      default: "px-2 pb-2 pt-0 @xl:px-5 @xl:py-8",
      sm: "px-2 pb-10 pt-0 @xl:px-4 @xl:py-6",
      lg: "px-2 pb-14 pt-0 @xl:px-6 @xl:py-10",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

const logoVariants = cva("shrink-0 relative", {
  variants: {
    size: {
      default: "w-[220px] h-[93px] @xl:w-[200px] @xl:h-[84px]",
      sm: "w-[180px] h-[76px] @xl:w-[160px] @xl:h-[67px]",
      lg: "w-[260px] h-[110px] @xl:w-[240px] @xl:h-[101px]",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

// PageTitle Component - Game page header with logo, categories, and actions
interface PageTitleProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof pageTitleVariants> {
  gameDetails: GetGameDetailsAPIResponse;
  badges?: CampaignQuestState[];
  socialStats?: SocialStatProps[];
  playUrl?: string;
  isWishlisted?: boolean;
  toggleWishlist?: () => void;
  handleTwitchDropsClick?: () => void;
}

const PageTitle = React.forwardRef<HTMLDivElement, PageTitleProps>(
  (
    {
      gameDetails,
      badges = [],
      playUrl,
      variant,
      size,
      className,
      isWishlisted,
      toggleWishlist,
      handleTwitchDropsClick,
      ...props
    },
    ref,
  ) => {
    const { trackGameEvent } = useGameDetailsAnalytics();
    const { isLoggedIn } = usePassportProvider();
    const socialStats: SocialStatProps[] = gameDetails.social_stats.map(
      (stat) => mapSocialStat(stat),
    );

    // Get quest completions to determine badge locked/unlocked state
    const { completedQuests } = useQuestCompletions(
      isLoggedIn ? gameDetails.game_id : undefined,
    );

    const onPlayClick = () => {
      trackGameEvent(Action.PRESSED, "PlayGame", gameDetails);
    };

    const twitchDropsLive = React.useMemo(() => {
      const now = new Date();
      return (
        gameDetails.slug === METATOY_GAME_UID &&
        now >= METATOY_TWITCH_DROPS_DATE &&
        now <= METATOY_TWITCH_DROPS_END_DATE
      );
    }, [gameDetails.slug]);

    return (
      <div
        ref={ref}
        className={cn(pageTitleVariants({ variant, size }), className)}
        {...props}
      >
        {/* Mobile: Vertical Layout | Desktop: Horizontal Layout */}
        <div className={cn(contentVariants({ size }), "flex flex-col gap-5")}>
          {/* Logo + Actions + Categories Section */}
          <div className="flex flex-col gap-5 @xl:flex-row @xl:items-center @xl:justify-between @xl:gap-5">
            {/* Mobile: Logo + Actions Section | Desktop: Logo + Categories */}
            <div className="flex items-center justify-between w-full @xl:justify-start @xl:w-auto @xl:gap-5">
              {/* Mobile: Spacer to center logo | Desktop: Hidden */}
              <div className="@xl:hidden shrink-0 size-9" />

              {/* Logo - Mobile: centered, Desktop: left */}
              {!!gameDetails.logo_url && (
                <div className={cn(logoVariants({ size }))}>
                  <Image
                    src={gameDetails.logo_url}
                    alt={gameDetails.name}
                    fill
                    sizes="220px"
                    className="object-contain"
                    priority
                  />
                </div>
              )}

              {/* Desktop: Categories Section (anchored beside logo) */}
              <CategoryBadges
                categories={gameDetails.genres}
                className="hidden @xl:flex"
              />

              {/* Mobile: Action Buttons (stacked vertically on right) */}
              <ActionButtons
                orientation="vertical"
                className="@xl:hidden"
                onPlayClick={onPlayClick}
                playUrl={playUrl}
                isWishlisted={isWishlisted}
                toggleWishlist={toggleWishlist}
                handleTwitchDropsClick={handleTwitchDropsClick}
                twitchDropsLive={twitchDropsLive}
              />
            </div>

            {/* Desktop: Action Buttons (right-aligned) */}
            <ActionButtons
              className="hidden @xl:flex"
              onPlayClick={onPlayClick}
              playUrl={playUrl}
              isWishlisted={isWishlisted}
              toggleWishlist={toggleWishlist}
              handleTwitchDropsClick={handleTwitchDropsClick}
              twitchDropsLive={twitchDropsLive}
            />

            {/* Mobile: Categories Section (centered below logo) */}
            <CategoryBadges
              categories={gameDetails.genres}
              className="justify-center w-full px-4 @xl:hidden"
            />
          </div>

          {/* Social Stats Section - Shows on both mobile and desktop */}
          {socialStats.length > 0 && (
            <div className="flex flex-col gap-5 @xl:flex-row @xl:flex-wrap @xl:items-center w-full">
              <div className="flex flex-wrap gap-5 items-center justify-center @xl:justify-start">
                {socialStats.map((stat, index) => (
                  <div
                    key={`${stat.value}-${stat.label}-${index}`}
                    className="flex items-center gap-1"
                  >
                    <div className="w-4 h-4 flex-shrink-0 [&>*]:w-full [&>*]:h-full">
                      {stat.icon}
                    </div>
                    <span className="font-medium text-xs text-white">
                      {stat.value}
                    </span>
                    <span className="font-normal text-xs text-white">
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>

              {badges
                .filter((badge) => completedQuests?.[badge.externalId])
                .map((badge) => {
                  const unlockedBadge = getUnlockedBadge(badge.type);
                  if (!unlockedBadge) return null;

                  const completionDate =
                    completedQuests?.[badge.externalId]?.latest_completion_time;
                  const tooltipData = unlockedBadge.getTooltipData(
                    gameDetails.name,
                    completionDate,
                  );

                  return (
                    <div
                      key={badge.externalId}
                      className="flex items-center justify-center @xl:justify-start"
                    >
                      <DisplayBadge
                        imageUrl={unlockedBadge.imageUrl}
                        alt={unlockedBadge.alt}
                        size={56}
                        tooltipData={tooltipData}
                        quest={badge}
                      />
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    );
  },
);

export { PageTitle };
export type { PageTitleProps };
